/* eslint-disable import/no-extraneous-dependencies */
// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Box,
  Typography,
  Fab,
  Dialog,
  Card,
  Zoom,
  Slide,
  Button,
} from '@mui/material';
import { useSelector } from 'react-redux';
// eslint-disable-next-line import/no-cycle
import TableWithComment from './Table/TableWithComment';
import IframeContainer from '../AllCustomComponents/Iframe/IframeContainer';
import OperationResultsViwerImageContainer from './OperationResultsViewerImageContainer';
import OperationResultsViewerDatasetTable from './OperationResultsViewerDatasetTable';
import DialogTransition from './Transitions/DialogTransition';
import MODULE_SUB_MODULE_OPERATIONS_TREE from '../../const/ModuleSubModuleOperationsTree';
import { SwalToast } from './index';

const GRID_HALF = 6;
const GRID_FULL = 12;
const SMALL_SPACING = 60;
const LARGE_SPACING = 80;
const MAX_THRESHOLD_FOR_SHOW_MORE_TABLES = 20;

/**
 * @function ConsoleOperationsResultViewer
 * @description To Console Operations Result Viewer
 * @param {object} taskResult   - An object which stores the task Result
 * @param {object} activeDataset - An object the dataset which are active
 * @param {string} operationName - It stores the name of operation
 * @param {any} customCell   - A value which store custom cell data
 * @param {boolean} showCommentBox - A boolean value which decide to show commentBox or not
 * @param {function} fetchTableData - A function to fetch Table data
 * @param {string} permission       - A string value
 * @param {function} downloadTableData - A function to download data of table
 * @param {bool} isPagination     - A boolean value
 * @returns {JSX.Element}
 * @example <ConsoleOperationsResultViewer taskResult={taskResult}/>
 */
const ConsoleOperationsResultViewer = ({
  fetchUDFTaskDetailsSplitAPI = {},
  taskResult = {},
  // activeDataset = {},
  operationName = '',
  customCell,
  showCommentBox = true,
  fetchTableData,
  permission,
  downloadTableData,
  isPagination,
  taskResultDatasets = [],
  fetchDatasetRowsWithPagination = () => {},
  isLoading = false,
  activeProject = {},
  datasetList = [],
  fetchAllDatasets,
  markDatasetAsReadyToUse,
  fetchModelMetaDataSchema,
  addResultToModelMetaData,
  enableComments = true,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [graphData, setGraphData] = useState([]);
  const [modelMetaDataSchema, setModelMetaDataSchema] = useState(null);
  const { permissions } = useSelector((state) => state.user);
  // const [tableData, setTableData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [imageData, setImageData] = useState([]);
  const [textData, setTextData] = useState([]);
  const [slicedTables, setSlicedTables] = useState(
    tableData?.length ? tableData?.slice(0, 10) : []
  );

  React.useEffect(() => {
    if (tableData?.length > MAX_THRESHOLD_FOR_SHOW_MORE_TABLES)
      setSlicedTables(tableData?.length ? tableData?.slice(0, 10) : []);
  }, [tableData]);

  const showMoreTables = React.useCallback(() => {
    if (tableData?.length) {
      setSlicedTables([
        ...slicedTables,
        ...tableData.slice(slicedTables?.length, slicedTables?.length + 10),
      ]);
    }
  }, [slicedTables, tableData]);

  const commentOnResultsPermission =
    permissions[
      `${MODULE_SUB_MODULE_OPERATIONS_TREE.AD.key}_${MODULE_SUB_MODULE_OPERATIONS_TREE.AD.subModules.CR.key}`
    ];

  /**
   * @function handleDialogOpen
   * @description To change the state of DialogOpen from null to true
   * @returns {void}
   * @example handleDialogOpen()
   */
  const handleDialogOpen = useCallback(() => {
    setDialogOpen(true);
  }, [setDialogOpen]);

  /**
   * @function handleDialogClose
   * @description It change the state from null to false
   * @returns {void}
   * @example handleDialogClose
   */
  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
  }, [setDialogOpen]);

  const fetchConsoleChartResult = useCallback(() => {
    if (
      !chartData?.charts ||
      chartData?.charts?.length === 0 ||
      taskResult?.task_id !== chartData?.task_id ||
      !chartData?.task_id
    ) {
      fetchUDFTaskDetailsSplitAPI(taskResult?.task_id, 'charts').then((res) => {
        setChartData(res?.data?.charts);
      });
    }
  }, [setChartData, fetchUDFTaskDetailsSplitAPI, taskResult?.task_id]);

  const fetchConsoleTableResult = useCallback(() => {
    if (
      !tableData?.tables ||
      tableData?.tables?.length === 0 ||
      taskResult?.task_id !== tableData?.task_id ||
      !tableData?.task_id
    ) {
      fetchUDFTaskDetailsSplitAPI(taskResult?.task_id, 'tables').then((res) => {
        setTableData(res?.data?.tables);
      });
    }
  }, [setTableData, fetchUDFTaskDetailsSplitAPI, taskResult?.task_id]);

  const fetchConsoleImageResult = useCallback(() => {
    if (
      !imageData?.images ||
      imageData?.images?.length === 0 ||
      taskResult?.task_id !== imageData?.task_id ||
      !imageData?.task_id
    ) {
      fetchUDFTaskDetailsSplitAPI(taskResult?.task_id, 'images').then((res) => {
        setImageData(res?.data?.images);
      });
    }
  }, [setImageData, fetchUDFTaskDetailsSplitAPI, taskResult?.task_id]);

  const fetchConsoleTextResult = useCallback(() => {
    if (
      !textData?.images ||
      textData?.images?.length === 0 ||
      taskResult?.task_id !== textData?.task_id ||
      !textData?.task_id
    ) {
      fetchUDFTaskDetailsSplitAPI(taskResult?.task_id, 'texts').then((res) => {
        setTextData(res?.data?.texts);
      });
    }
  }, [setTextData, fetchUDFTaskDetailsSplitAPI, taskResult?.task_id]);

  const handleNoTaskResult = useCallback(() => {
    SwalToast({
      icon: 'info',
      title: 'No results were generated by this operation.',
    });
  }, []);

  const [activeView, setActiveView] = useState('table');
  useEffect(() => {
    if (taskResult?.is_chart) {
      setActiveView('graph');
      fetchConsoleChartResult();
    } else if (taskResult?.exceptions) {
      setActiveView('exceptions');
    } else if (taskResult?.is_image) {
      setActiveView('image');
      fetchConsoleImageResult();
    } else if (taskResult?.is_table) {
      setActiveView('table');
      fetchConsoleTableResult();
    } else if (taskResult?.isText) {
      setActiveView('text');
      fetchConsoleTextResult();
    } else if (taskResult?.is_grid) {
      setActiveView('dataset');
    }
  }, [taskResult]);

  const handleExceptionsClick = useCallback(() => {
    setActiveView('exceptions');
  }, [setActiveView]);

  const handleParametersClick = useCallback(() => {
    setActiveView('parameters');
  }, [setActiveView]);

  const handleTableClick = useCallback(() => {
    setActiveView('table');
    fetchConsoleTableResult();
  }, [setActiveView, taskResult]);

  const handleGraphClick = useCallback(() => {
    setActiveView('graph');
    fetchConsoleChartResult();
  }, [setActiveView, taskResult, chartData]);

  const handleImageClick = useCallback(() => {
    setActiveView('image');
    fetchConsoleImageResult();
  }, [setActiveView, taskResult]);

  const handleTextClick = useCallback(() => {
    setActiveView('text');
    fetchConsoleTextResult();
  }, [setActiveView, taskResult]);

  const handleDatasetClick = useCallback(() => {
    setActiveView('dataset');
  }, [setActiveView, taskResult]);

  const getHeight = useCallback(
    (theme) => ({
      height:
        chartData?.length > 1
          ? theme.spacing(SMALL_SPACING)
          : theme.spacing(LARGE_SPACING),
    }),
    []
  );

  React.useEffect(() => {
    if (
      // eslint-disable-next-line no-constant-condition
      fetchModelMetaDataSchema &&
      activeProject?.project_id &&
      activeProject?.system_generated_flag &&
      activeProject?.system_name === 'Vault'
    ) {
      fetchModelMetaDataSchema({
        project_id: activeProject?.project_id,
        type: activeProject?.type,
      }).then(
        (resp) => {
          setModelMetaDataSchema(resp?.data);
        },
        () => {
          setModelMetaDataSchema(null);
        }
      );
    }
  }, []);

  useEffect(() => {
    if (
      taskResult &&
      Object.keys(taskResult).length > 0 &&
      !taskResult.is_chart &&
      !taskResult.is_table &&
      !taskResult.is_image &&
      !taskResult.is_grid &&
      !taskResult.isText
    ) {
      handleNoTaskResult();
    }
  }, [taskResult]);

  return (
    <Grid container mt="20px">
      <Box
        sx={{
          position: 'fixed',
          bottom: '30px',
          right: ' 30px',
          zIndex: 1000,
        }}
      >
        {taskResult?.is_chart ||
        taskResult?.exceptions ||
        taskResult?.parameters ||
        taskResult?.is_table ||
        taskResult?.is_image ||
        taskResult?.is_grid ||
        taskResult?.isText ? (
          <Slide direction="left" in={true} timeout={900}>
            <Fab
              variant="extended"
              size="small"
              disableRipple={true}
              disableFocusRipple={true}
              sx={{
                padding: 0,
                ':hover': { backgroundColor: 'secondary.main' },
              }}
              color="secondary"
            >
              {taskResult?.exceptions ? (
                <Fab
                  variant="extended"
                  size="small"
                  sx={{
                    boxShadow: activeView === 'exceptions' ? 1 : 0,
                  }}
                  color={activeView === 'exceptions' ? 'primary' : 'secondary'}
                  onClick={handleExceptionsClick}
                >
                  Exceptions
                </Fab>
              ) : null}
              {taskResult?.parameters ? (
                <Fab
                  variant="extended"
                  size="small"
                  sx={{
                    boxShadow: activeView === 'parameters' ? 1 : 0,
                  }}
                  color={activeView === 'parameters' ? 'primary' : 'secondary'}
                  onClick={handleParametersClick}
                >
                  Parameters
                </Fab>
              ) : null}
              {taskResult?.is_table ? (
                <Fab
                  variant="extended"
                  size="small"
                  sx={{
                    boxShadow: activeView === 'table' ? 1 : 0,
                  }}
                  color={activeView === 'table' ? 'primary' : 'secondary'}
                  onClick={handleTableClick}
                >
                  Tables
                </Fab>
              ) : null}
              {taskResultDatasets?.length && taskResult?.is_grid ? (
                <Fab
                  variant="extended"
                  size="small"
                  sx={{
                    boxShadow: activeView === 'dataset' ? 'block' : 'none',
                  }}
                  color={activeView === 'dataset' ? 'primary' : 'secondary'}
                  onClick={handleDatasetClick}
                >
                  Dataset
                </Fab>
              ) : null}
              {taskResult?.is_chart ? (
                <Fab
                  variant="extended"
                  sx={{
                    boxShadow: activeView === 'graph' ? 1 : 0,
                  }}
                  color={activeView === 'graph' ? 'primary' : 'secondary'}
                  size="small"
                  onClick={handleGraphClick}
                >
                  CHARTS
                </Fab>
              ) : null}
              {taskResult?.is_image ? (
                <Fab
                  variant="extended"
                  sx={{
                    boxShadow: activeView === 'image' ? 1 : 0,
                  }}
                  color={activeView === 'image' ? 'primary' : 'secondary'}
                  size="small"
                  onClick={handleImageClick}
                >
                  Images
                </Fab>
              ) : null}
              {taskResult?.isText ? (
                <Fab
                  variant="extended"
                  sx={{
                    boxShadow: activeView === 'text' ? 1 : 0,
                  }}
                  color={activeView === 'text' ? 'primary' : 'secondary'}
                  size="small"
                  onClick={handleTextClick}
                >
                  Text
                </Fab>
              ) : null}
            </Fab>
          </Slide>
        ) : null}
      </Box>

      {/*
				<Box display="flex" alignItems="center" mb={2} mt={3}></Box> */}

      {taskResult && Object.keys(taskResult).length !== 0 && (
        <Box width="100%" pb={5}>
          <Grid container spacing={3}>
            {taskResult?.exceptions ? (
              <Grid
                item
                xs={12}
                style={{
                  display: activeView === 'exceptions' ? 'block' : 'none',
                }}
              >
                <Slide
                  direction="up"
                  in={activeView === 'exceptions'}
                  timeout={900}
                >
                  <Box mt={1} mb={1}>
                    <Card>
                      <Box pt={2} pb={2} pr={3} pl={3}>
                        <Typography>
                          <pre
                            style={{
                              whiteSpace: 'pre-wrap',
                              wordWrap: 'break-word',
                              wordBreak: 'keep-all',
                            }}
                          >
                            {taskResult?.exceptions}
                          </pre>
                        </Typography>
                      </Box>
                    </Card>
                  </Box>
                </Slide>
              </Grid>
            ) : null}

            {textData?.length && taskResult?.isText ? (
              <Slide direction="up" in={activeView === 'text'} timeout={900}>
                <Grid
                  item
                  xs={12}
                  style={{ display: activeView === 'text' ? 'block' : 'none' }}
                >
                  {textData.map((text) => (
                    <Box m={1} key={text.id}>
                      <Card>
                        <Box pt={2} pb={2} pr={3} pl={3}>
                          <Typography tabIndex={0}>
                            <pre
                              style={{
                                whiteSpace: 'pre-wrap',
                                wordWrap: 'break-word',
                                wordBreak: 'keep-all',
                              }}
                            >
                              {text?.string}
                            </pre>
                          </Typography>
                        </Box>
                      </Card>
                    </Box>
                  ))}
                </Grid>
              </Slide>
            ) : null}

            {tableData?.length && taskResult?.is_table ? (
              <Slide direction="up" in={activeView === 'table'} timeout={900}>
                <Grid
                  item
                  xs={12}
                  style={{ display: activeView === 'table' ? 'block' : 'none' }}
                >
                  <Box>
                    {(tableData?.length > MAX_THRESHOLD_FOR_SHOW_MORE_TABLES
                      ? slicedTables
                      : tableData
                    )?.map((data) => (
                      <Box mb={4} key={`tables_${data.id}`}>
                        <TableWithComment
                          type="outputTable"
                          tableData={data}
                          opName={operationName || ''}
                          comment={showCommentBox}
                          commentid={data?.id}
                          // commenttableName={
                          //   activeDataset?.Name ? activeDataset?.Name : ''
                          // }
                          initialPageSize={
                            tableData?.length >
                            MAX_THRESHOLD_FOR_SHOW_MORE_TABLES
                              ? 5
                              : undefined
                          }
                          customCell={customCell}
                          isConsole
                          fetchTableData={fetchTableData}
                          permission={permission}
                          downloadTableData={downloadTableData}
                          isPaginated={isPagination}
                          modelMetaDataSchema={modelMetaDataSchema}
                          addResultToModelMetaData={addResultToModelMetaData}
                          fetchModelMetaDataSchema={fetchModelMetaDataSchema}
                          activeProject={activeProject}
                          commentOnResultsPermission={
                            commentOnResultsPermission
                          }
                        />
                      </Box>
                    ))}
                  </Box>
                  {tableData?.length > 20 &&
                  tableData?.length !== slicedTables?.length ? (
                    <Box
                      display="flex"
                      alignItems="center"
                      flexDirection="column"
                    >
                      <Button onClick={showMoreTables} variant="contained">
                        Show more tables
                      </Button>
                    </Box>
                  ) : null}
                </Grid>
              </Slide>
            ) : null}

            {taskResultDatasets?.length && taskResult?.is_grid ? (
              <Slide direction="up" in={activeView === 'dataset'} timeout={900}>
                <Grid
                  item
                  xs={12}
                  style={{
                    display: activeView === 'dataset' ? 'block' : 'none',
                  }}
                >
                  <Box>
                    <OperationResultsViewerDatasetTable
                      fetchDatasetRowsWithPagination={
                        fetchDatasetRowsWithPagination
                      }
                      isLoading={isLoading}
                      projectId={activeProject?.project_id}
                      activeProject={activeProject}
                      datasetList={datasetList?.length ? datasetList : []}
                      permissions={permission}
                      taskResultDatasets={taskResultDatasets}
                      fetchAllDatasets={fetchAllDatasets}
                      markDatasetAsReadyToUse={markDatasetAsReadyToUse}
                      enableComments={enableComments}
                    />
                  </Box>
                </Grid>
              </Slide>
            ) : null}

            {chartData?.length && taskResult?.is_chart ? (
              <Slide direction="up" in={activeView === 'graph'} timeout={900}>
                <Grid
                  item
                  xs={12}
                  style={{ display: activeView === 'graph' ? 'block' : 'none' }}
                >
                  <Box my={0}>
                    <Grid container spacing={5}>
                      {chartData?.map((data) => (
                        <Grid
                          item
                          xs={12}
                          sm={chartData?.length > 1 ? GRID_HALF : GRID_FULL}
                          key={`graph_${data.id}`}
                        >
                          <Zoom in={true}>
                            <Card sx={getHeight}>
                              <Box
                                display="flex"
                                flexDirection="column"
                                height="100%"
                                pl={1}
                                pr={1}
                                pt={1}
                                pb={1}
                              >
                                <Box display="flex" flexGrow={1}>
                                  <Box sx={{ width: '100%', height: '100%' }}>
                                    <IframeContainer
                                      key={data.id}
                                      url={data.url}
                                      msg={data.msg}
                                      b64={data?.b64}
                                      status={200}
                                      showCommentBox={showCommentBox}
                                      commentid={data.id}
                                      chartHeader={data?.chart_title}
                                      // commenttableName={activeDataset.Name}
                                      commentButtonStyle={{
                                        position: 'absolute',
                                        top: 17,
                                        right: 65,
                                      }}
                                      isConsole
                                      graphData={data}
                                      dialogOpen={dialogOpen}
                                      handleDialogOpen={handleDialogOpen}
                                      setGraphData={setGraphData}
                                      handleDialogClose={handleDialogClose}
                                      permission={permission}
                                      activeProject={activeProject}
                                      modelMetaDataSchema={modelMetaDataSchema}
                                      addResultToModelMetaData={
                                        addResultToModelMetaData
                                      }
                                      commentOnResultsPermission={
                                        commentOnResultsPermission
                                      }
                                    />
                                  </Box>
                                </Box>
                              </Box>
                            </Card>
                          </Zoom>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Grid>
              </Slide>
            ) : null}
            {imageData?.length && taskResult?.is_image ? (
              <Slide direction="up" in={activeView === 'image'} timeout={900}>
                <Grid
                  item
                  xs={12}
                  style={{ display: activeView === 'image' ? 'block' : 'none' }}
                >
                  <Box my={0}>
                    <Grid container spacing={5}>
                      {imageData?.map((data) => (
                        <Grid
                          item
                          xs={12}
                          sm={imageData?.length > 1 ? GRID_HALF : GRID_FULL}
                          key={`images_${data.id}`}
                        >
                          <Zoom in={true}>
                            <Card
                              sx={(theme) => ({
                                height:
                                  chartData?.length > 1
                                    ? theme.spacing(SMALL_SPACING)
                                    : '100%',
                              })}
                            >
                              <Box
                                display="flex"
                                flexDirection="column"
                                height="100%"
                                pl={1}
                                pr={1}
                                pt={1}
                                pb={1}
                              >
                                <Box display="flex" flexGrow={1}>
                                  <Box sx={{ width: '100%', height: '100%' }}>
                                    <OperationResultsViwerImageContainer
                                      url={data.url}
                                      showCommentBox={showCommentBox}
                                      commentId={data.id}
                                      b64={data?.b64}
                                      // commentTableName={activeDataset.Name}
                                      imageHeader={data?.image_title}
                                      isConsole
                                      imgData={data}
                                      dialogOpen={dialogOpen}
                                      handleDialogOpen={handleDialogOpen}
                                      setImageData={setGraphData}
                                      handleDialogClose={handleDialogClose}
                                      permission={permission}
                                      activeProject={activeProject}
                                      modelMetaDataSchema={modelMetaDataSchema}
                                      addResultToModelMetaData={
                                        addResultToModelMetaData
                                      }
                                      commentOnResultsPermission={
                                        commentOnResultsPermission
                                      }
                                    />
                                  </Box>
                                </Box>
                              </Box>
                            </Card>
                          </Zoom>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Grid>
              </Slide>
            ) : null}

            <Dialog
              fullWidth
              aria-labelledby="customized-dialog-title"
              maxWidth="xl"
              open={dialogOpen}
              TransitionComponent={DialogTransition}
            >
              <Box height={650} flexGrow={1}>
                {graphData?.type === 'chart' ? (
                  <IframeContainer
                    url={graphData.url}
                    msg={graphData.msg}
                    status={200}
                    b64={graphData?.b64}
                    showCommentBox={showCommentBox}
                    commentid={graphData.id}
                    chartHeader={graphData?.chart_title}
                    isConsole
                    graphData={graphData}
                    dialogOpen={dialogOpen}
                    handleDialogOpen={handleDialogOpen}
                    setGraphData={setGraphData}
                    handleDialogClose={handleDialogClose}
                    permission={permission}
                    activeProject={activeProject}
                    modelMetaDataSchema={modelMetaDataSchema}
                    addResultToModelMetaData={addResultToModelMetaData}
                    commentOnResultsPermission={commentOnResultsPermission}
                  />
                ) : (
                  <OperationResultsViwerImageContainer
                    url={graphData.url}
                    b64={graphData?.b64}
                    showCommentBox={showCommentBox}
                    commentId={graphData.id}
                    imageHeader={graphData?.image_title}
                    imgData={graphData}
                    isConsole
                    dialogOpen={dialogOpen}
                    handleDialogOpen={handleDialogOpen}
                    setImageData={setGraphData}
                    handleDialogClose={handleDialogClose}
                    permission={permission}
                    activeProject={activeProject}
                    modelMetaDataSchema={modelMetaDataSchema}
                    addResultToModelMetaData={addResultToModelMetaData}
                    commentOnResultsPermission={commentOnResultsPermission}
                  />
                )}
              </Box>
            </Dialog>
          </Grid>
        </Box>
      )}
    </Grid>
  );
};
ConsoleOperationsResultViewer.defaultProps = {
  fetchUDFTaskDetailsSplitAPI: {},
  customCell: null,
  operationName: '',
  taskResult: {},
  permission: 'Manage',
  downloadTableData: () => ({}),
  isPagination: false,
  taskResultDatasets: [],
  activeProject: {},
  fetchDatasetRowsWithPagination: () => {},
  isLoading: false,
  datasetList: [],
  fetchAllDatasets: () => {},
  markDatasetAsReadyToUse: () => {},
  fetchModelMetaDataSchema: null,
  addResultToModelMetaData: null,
  enableComments: true,
};
ConsoleOperationsResultViewer.propTypes = {
  taskResult: PropTypes.oneOfType([PropTypes.object]),
  // activeDataset: PropTypes.oneOfType([PropTypes.object, PropTypes.string])
  //   .isRequired,
  operationName: PropTypes.string,
  customCell: PropTypes.oneOfType([PropTypes.object]),
  showCommentBox: PropTypes.bool.isRequired,
  fetchTableData: PropTypes.func.isRequired,
  fetchUDFTaskDetailsSplitAPI: PropTypes.func,
  permission: PropTypes.string,
  downloadTableData: PropTypes.func,
  isPagination: PropTypes.bool,
  enableComments: PropTypes.bool,
  activeProject: PropTypes.oneOfType([PropTypes.object]),
  taskResultDatasets: PropTypes.oneOfType([PropTypes.array]),
  fetchDatasetRowsWithPagination: PropTypes.func,
  isLoading: PropTypes.bool,
  datasetList: PropTypes.oneOfType([PropTypes.array]),
  fetchAllDatasets: PropTypes.func,
  markDatasetAsReadyToUse: PropTypes.func,
  fetchModelMetaDataSchema: PropTypes.func,
  addResultToModelMetaData: PropTypes.func,
};
export default React.memo(ConsoleOperationsResultViewer);
